import React from 'react'
import PropTypes from 'prop-types'
import { useQuery } from 'react-apollo'

import AchievementsLiveLessonsStatsPlaceholder from 'src/pages/AchievementsLiveLessons/subcomponents/AchievementsLiveLessonsStats/AchievementsLiveLessonsStatsPlaceholder'
import AllTimeStatsWidget from 'src/pages/Achievements/subcomponents/AllTimeStatsWidget/AllTimeStatsWidget'
import AchievementWidget from 'src/pages/Achievements/subcomponents/AchievementWidget/AchievementWidget'
import FlashcardsWidget from 'src/pages/Achievements/subcomponents/FlashcardsWidget/FlashcardsWidget'
import FlashcardsWidgetPlaceholder from 'src/pages/Achievements/subcomponents/FlashcardsWidget/FlashcardsWidgetPlaceholder'
import AchievementWidgetPlaceholder from 'src/pages/Achievements/subcomponents/AchievementWidget/AchievementWidgetPlaceholder'
import Achievements from 'src/pages/Achievements/Achievements'
import ProgressWidget from 'src/pages/Achievements/subcomponents/ProgressWidget/ProgressWidget'
import ProgressWidgetPlaceholder from 'src/pages/Achievements/subcomponents/ProgressWidget/ProgressWidgetPlaceholder'
import CertWidget from 'src/pages/Achievements/subcomponents/CertWidget/CertWidget'
import CertWidgetPlaceholder from 'src/pages/Achievements/subcomponents/CertWidget/CertWidgetPlaceholder'
import Leaderboard from 'src/pages/Achievements/subcomponents/Leaderboard/Leaderboard'
import LeaderboardPlaceholder from 'src/pages/Achievements/subcomponents/Leaderboard/LeaderboardPlaceholder'

import {
  mapDataToAchievementsProps,
  mapDataToAllTimeStatsProps,
  mapDataToAppointmentProps,
  mapDataToProgressProps,
  mapDataToCardProps,
} from './util/util'
import {
  FETCH_PROGRESS_AND_CERTIFICATIONS,
  FETCH_WRITING_DATA,
  FETCH_MEDIA_DATA,
  FETCH_CONCEPTS_DATA,
  FETCH_APPOINTMENT_DATA,
  FETCH_STATS_DATA,
  FETCH_FLASHCARDS_DATA,
  FETCH_LEADERBOARD_DATA,
} from './queries'

const DATASETS_STYLES = {
  lineTension: 0.3,
  pointBorderWidth: 3,
  pointHoverBorderWidth: 3,
  pointHitRadius: 10,
  pointRadius: 0,
  pointHoverRadius: 4,
}

const AchievementsLazy = ({ l2Code, isOrg }) => {
  const progressAndCertificationsData = useQuery(
    FETCH_PROGRESS_AND_CERTIFICATIONS,
    {
      variables: {
        l2Code,
      },
    }
  )
  const {
    data: progressAndCertifications,
    loading: progressAndCertificationsLoading,
  } = progressAndCertificationsData

  const flashcardStatsData = useQuery(FETCH_FLASHCARDS_DATA, {
    variables: {
      l2Code,
    },
  })
  const {
    data: flashcardStats,
    loading: flashcardStatsLoading,
  } = flashcardStatsData

  const statsData = useQuery(FETCH_STATS_DATA, {
    variables: {
      l2Code,
    },
  })
  const { data: stats, loading: statsLoading } = statsData

  const mediaData = useQuery(FETCH_MEDIA_DATA, {
    variables: {
      l2Code,
    },
  })
  const { data: media, loading: mediaLoading } = mediaData

  const writingData = useQuery(FETCH_WRITING_DATA, {
    variables: {
      l2Code,
    },
  })
  const { data: writing, loading: writingLoading } = writingData

  const conceptData = useQuery(FETCH_CONCEPTS_DATA, {
    variables: {
      l2Code,
    },
  })
  const { data: concept, loading: conceptLoading } = conceptData

  const appointmentData = useQuery(FETCH_APPOINTMENT_DATA, {
    variables: {
      l2Code,
    },
  })
  const { data: appointment, loading: appointmentLoading } = appointmentData

  const leaderboardResponse = useQuery(FETCH_LEADERBOARD_DATA)

  const {
    data: leaderboardData,
    loading: leaderboardLoading,
  } = leaderboardResponse
  const leaderboardtopUsers = leaderboardData?.leaderBoard?.topPositions
  const leaderboardCurrentUser =
    leaderboardData?.leaderBoard?.currentUserPosition
  const orgSize = leaderboardData?.leaderBoard?.countOfMembers
  const orgName = leaderboardData?.leaderBoard?.name

  const userProgress = progressAndCertifications?.currentUser?.progress

  const cardInfo = mapDataToCardProps(
    flashcardStats,
    userProgress,
    DATASETS_STYLES
  )

  return (
    <Achievements
      progressSection={
        progressAndCertificationsLoading ? (
          <ProgressWidgetPlaceholder />
        ) : (
          <ProgressWidget
            goal={userProgress?.cefrLevel?.code}
            completed={Math.floor(userProgress?.cefrLevelProgress * 100)}
            l2Code={l2Code}
          />
        )
      }
      allTimeStatsSection={
        statsLoading ? (
          <AchievementsLiveLessonsStatsPlaceholder />
        ) : (
          <AllTimeStatsWidget
            {...mapDataToAllTimeStatsProps(stats, cardInfo?.totalCards)}
          />
        )
      }
      flashcardsSection={
        flashcardStatsLoading ? (
          <FlashcardsWidgetPlaceholder />
        ) : (
          <FlashcardsWidget {...cardInfo} l2Code={l2Code} />
        )
      }
      mediaSection={
        mediaLoading ? (
          <AchievementWidgetPlaceholder />
        ) : (
          <AchievementWidget
            {...mapDataToAchievementsProps(media, 'media', 'media_completed')}
          />
        )
      }
      writingSection={
        writingLoading ? (
          <AchievementWidgetPlaceholder />
        ) : (
          <AchievementWidget
            {...mapDataToAchievementsProps(
              writing,
              'writing',
              'writing_completed'
            )}
          />
        )
      }
      liveLessonSection={
        appointmentLoading ? (
          <AchievementWidgetPlaceholder />
        ) : (
          <AchievementWidget {...mapDataToAppointmentProps(appointment)} />
        )
      }
      conceptsSection={
        conceptLoading ? (
          <AchievementWidgetPlaceholder />
        ) : (
          <AchievementWidget
            {...mapDataToAchievementsProps(
              concept,
              'concept',
              'concepts_completed'
            )}
          />
        )
      }
      certSection={
        progressAndCertificationsLoading ? (
          <CertWidgetPlaceholder />
        ) : (
          <CertWidget
            certifications={mapDataToProgressProps(progressAndCertifications)}
          />
        )
      }
      leaderboardSection={
        !isOrg ? null : leaderboardLoading ? (
          <LeaderboardPlaceholder />
        ) : (
          <Leaderboard
            topUsers={leaderboardtopUsers}
            currentUserPosition={leaderboardCurrentUser}
            orgSize={orgSize}
            orgName={orgName}
          />
        )
      }
    />
  )
}

AchievementsLazy.propTypes = {
  l2Code: PropTypes.string.isRequired,
  isOrg: PropTypes.bool.isRequired,
}

export default AchievementsLazy
